import React, { Component } from 'react';
import { ArrowUpRight, ArrowDownRight, Download } from 'lucide-react';
// --- Sub-components ---
interface KPICardProps {
  title: string;
  value: string;
  trend: number;
  trendLabel?: string;
  inverse?: boolean;
}
const KPICard = ({
  title,
  value,
  trend,
  trendLabel = 'vs prior period',
  inverse = false
}: KPICardProps) => {
  const isPositiveTrend = trend > 0;
  const isGood = inverse ? !isPositiveTrend : isPositiveTrend;
  const trendColor = isGood ? 'text-green-600' : 'text-red-600';
  const TrendIcon = isPositiveTrend ? ArrowUpRight : ArrowDownRight;
  return <div className="bg-white border border-neutral-200 rounded-lg p-6" data-id="element-1505">
      <h3 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-3" data-id="element-1506">
        {title}
      </h3>
      <div className="text-3xl font-bold text-black mb-3" data-id="element-1507">{value}</div>
      <div className="flex items-center gap-2" data-id="element-1508">
        <div className={`flex items-center text-sm font-medium ${trendColor}`} data-id="element-1509">
          <TrendIcon size={14} className="mr-0.5" data-id="element-1510" />
          {Math.abs(trend)}%
        </div>
        <span className="text-xs text-neutral-400" data-id="element-1511">{trendLabel}</span>
      </div>
    </div>;
};
interface LossRatioBarProps {
  label: string;
  value: number;
  target: number;
}
const LossRatioBar = ({
  label,
  value,
  target
}: LossRatioBarProps) => {
  const variance = value - target;
  const isOverTarget = variance > 0;
  return <div className="mb-6 last:mb-0" data-id="element-1512">
      <div className="flex justify-between items-end mb-2" data-id="element-1513">
        <span className="text-sm font-medium text-neutral-700" data-id="element-1514">{label}</span>
        <div className="text-right" data-id="element-1515">
          <span className="text-xl font-bold text-black" data-id="element-1516">
            {value.toFixed(1)}%
          </span>
          <span className={`text-xs ml-2 ${isOverTarget ? 'text-red-600' : 'text-green-600'}`} data-id="element-1517">
            {isOverTarget ? '+' : ''}
            {variance.toFixed(1)}% vs target
          </span>
        </div>
      </div>
      <div className="relative h-2 bg-neutral-100 rounded-full overflow-hidden" data-id="element-1518">
        <div className="absolute top-0 bottom-0 w-0.5 bg-neutral-400 z-10" style={{
        left: `${target}%`
      }} data-id="element-1519" />
        <div className="h-full rounded-full transition-all duration-700 ease-out bg-purple-500" style={{
        width: `${Math.min(value, 100)}%`
      }} data-id="element-1520" />
      </div>
    </div>;
};
interface AlertItemProps {
  severity: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  date: string;
}
const AlertItem = ({
  severity,
  title,
  description,
  date
}: AlertItemProps) => {
  const severityColors = {
    high: 'bg-red-500',
    medium: 'bg-purple-500',
    low: 'bg-neutral-400'
  };
  return <div className="flex items-start p-4 border-b border-neutral-100 last:border-0" data-id="element-1521">
      <div className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${severityColors[severity]}`} data-id="element-1522" />
      <div className="ml-4 flex-1" data-id="element-1523">
        <div className="flex justify-between items-start" data-id="element-1524">
          <h4 className="text-sm font-medium text-black" data-id="element-1525">{title}</h4>
          <span className="text-xs text-neutral-400" data-id="element-1526">{date}</span>
        </div>
        <p className="text-sm text-neutral-600 mt-1" data-id="element-1527">{description}</p>
      </div>
    </div>;
};
// --- Main Component ---
export function ExecutiveSummary() {
  return <div className="space-y-8 animate-in" data-id="element-1528">
      {/* Header */}
      <div className="flex justify-between items-start" data-id="element-1529">
        <div data-id="element-1530">
          <h1 className="text-2xl font-bold text-black" data-id="element-1531">Executive Summary</h1>
          <p className="text-neutral-500 mt-1" data-id="element-1532">
            Portfolio health at a glance • Q4 2024
          </p>
        </div>
        <button className="px-4 py-2 bg-pink-500 text-white rounded-md text-sm font-medium hover:bg-pink-600 transition-colors flex items-center gap-2" data-id="element-1533">
          <Download size={16} data-id="element-1534" />
          Export
        </button>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-1535">
        <KPICard title="Gross Written Premium" value="£12.5M" trend={8.5} data-id="element-1536" />
        <KPICard title="Claims Incurred" value="£4.8M" trend={-3.2} inverse={true} data-id="element-1537" />
        <KPICard title="Underwriting Profit" value="£2.1M" trend={12.4} data-id="element-1538" />
        <KPICard title="Capital Held" value="£8.5M" trend={5.1} data-id="element-1539" />
        <KPICard title="Return on Capital" value="24.7%" trend={2.3} data-id="element-1540" />
      </div>

      {/* Middle Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1541">
        {/* Loss Ratios Chart */}
        <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-lg p-6" data-id="element-1542">
          <div className="flex justify-between items-center mb-6" data-id="element-1543">
            <h2 className="text-lg font-medium text-black" data-id="element-1544">
              Loss Ratios Analysis
            </h2>
            <div className="flex gap-6 text-xs text-neutral-500" data-id="element-1545">
              <div className="flex items-center gap-2" data-id="element-1546">
                <div className="w-3 h-3 bg-purple-500 rounded-full" data-id="element-1547"></div>
                <span data-id="element-1548">Actual</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1549">
                <div className="w-0.5 h-3 bg-neutral-400" data-id="element-1550"></div>
                <span data-id="element-1551">Target</span>
              </div>
            </div>
          </div>

          <div className="space-y-6" data-id="element-1552">
            <LossRatioBar label="Gross Loss Ratio (GLR)" value={62.5} target={60.0} data-id="element-1553" />
            <LossRatioBar label="Expected Loss Ratio (ELR)" value={58.0} target={60.0} data-id="element-1554" />
            <LossRatioBar label="Net Loss Ratio (NLR)" value={55.3} target={58.0} data-id="element-1555" />
          </div>

          <div className="mt-8 pt-6 border-t border-neutral-100 grid grid-cols-3 gap-4" data-id="element-1556">
            <div className="text-center" data-id="element-1557">
              <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-1" data-id="element-1558">
                YTD Variance
              </p>
              <p className="text-xl font-bold text-red-600" data-id="element-1559">+2.5%</p>
            </div>
            <div className="text-center border-l border-neutral-200" data-id="element-1560">
              <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-1" data-id="element-1561">
                Prior Year
              </p>
              <p className="text-xl font-bold text-black" data-id="element-1562">61.2%</p>
            </div>
            <div className="text-center border-l border-neutral-200" data-id="element-1563">
              <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-1" data-id="element-1564">
                Forecast
              </p>
              <p className="text-xl font-bold text-black" data-id="element-1565">60.5%</p>
            </div>
          </div>
        </div>

        {/* Alerts & Exceptions */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6" data-id="element-1566">
          <div className="flex justify-between items-center mb-4" data-id="element-1567">
            <h2 className="text-lg font-medium text-black" data-id="element-1568">Alerts</h2>
            <span className="bg-pink-100 text-pink-600 text-xs font-medium px-2 py-1 rounded-full" data-id="element-1569">
              3 New
            </span>
          </div>

          <div data-id="element-1570">
            <AlertItem severity="high" title="Large Loss Reported" description="Claim ref #CLM-2024-892 exceeds £450k threshold." date="Today" data-id="element-1571" />
            <AlertItem severity="medium" title="Credit Control Alert" description="3 broker accounts overdue > 60 days." date="Yesterday" data-id="element-1572" />
            <AlertItem severity="low" title="Accumulation Breach" description="Florida wind exposure at 85% of limit." date="2 days ago" data-id="element-1573" />
          </div>

          <button className="w-full mt-4 py-2 text-sm text-pink-600 font-medium hover:bg-pink-50 rounded-md transition-colors" data-id="element-1574">
            View All Alerts
          </button>
        </div>
      </div>
    </div>;
}